import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  Alert,
  InputAdornment,
  Paper,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { api } from "../../utils/api";
import ServiceWorkflow from "./ServiceWorkflow";

const CATEGORIES = [
  "Accounting",
  "Audit",
  "Book Keeping",
  "Business Setup",
  "Certification",
  "Company Compliance",
  "Corporate Law",
  "Due Diligence",
  "Financial Reporting",
  "General",
  "GST",
  "Income Tax",
  "Internal Audit",
  "Legal Drafting",
  "Licenses & Registrations",
  "Payroll",
  "Registration",
  "Risk Advisory",
  "ROC / MCA",
  "Secretarial Services",
  "Statutory Audit",
  "Taxation",
  "TDS",
  "Valuation",
  "Balance Sheet",
  "NOT APPLICABLE",
];

const FREQUENCIES = [
  { value: "one_time", label: "One Time" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "annual", label: "Annual" },
];

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const monthShort = (m) => {
  const obj = MONTHS.find((x) => x.value === Number(m));
  return obj ? obj.label.substring(0, 3) : "";
};

// Generate cycle preview text based on frequency + due_month + due_day
const getCyclePreview = (freq, dueMonth, dueDay) => {
  if (!freq) return "Select frequency to see cycle info";
  if (freq === "one_time") {
    return "Due date will be set when service is assigned to a job.";
  }
  if (freq === "monthly") {
    if (!dueDay) return "Every month — pick a due day";
    return `Every month on day ${dueDay}`;
  }
  if (!dueMonth || !dueDay) {
    return "Pick month & day to see cycle";
  }
  const m = Number(dueMonth);
  const d = Number(dueDay);
  if (freq === "quarterly") {
    const months = [
      m,
      ((m + 2) % 12) + 1,
      ((m + 5) % 12) + 1,
      ((m + 8) % 12) + 1,
    ]
      .map(monthShort)
      .join(" · ");
    return `Cycles: ${months} — Day ${d}`;
  }
  if (freq === "half_yearly") {
    const second = ((m + 5) % 12) + 1;
    return `Cycles: ${monthShort(m)} · ${monthShort(second)} — Day ${d}`;
  }
  if (freq === "annual") {
    return `Every year: ${monthShort(m)} ${d}`;
  }
  return "";
};

const REQUIRED_BASE = ["name", "frequency", "default_price"];

export default function ServiceForm({ data, onSuccess, onClose }) {
  const isEdit = !!data;
  const [managers, setManagers] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    frequency: "",
    default_price: "",
    due_day: "",
    due_month: "",
    create_before_days: "5",
    sla_days: "7",
    service_type: "",
    default_manager_id: "",
    audit_required: 0,
    is_active: 1,
  });

  useEffect(() => {
    if (isEdit && data) {
      // Filter out null/undefined so defaults (sla_days, create_before_days) are preserved
      const cleaned = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== null && v !== undefined),
      );
      setForm((f) => ({ ...f, ...cleaned }));
    }
  }, [data, isEdit]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/users/list.php");
        const mgrs = (res.users || []).filter((u) => u.role === "Manager");
        setManagers(mgrs);
        if (!isEdit && mgrs.length > 0) {
          setForm((f) => ({ ...f, default_manager_id: mgrs[0].id }));
        }
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const setField = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Auto-clear irrelevant fields when frequency changes
      if (key === "frequency") {
        if (value === "one_time") {
          next.due_day = "";
          next.due_month = "";
        } else if (value === "monthly") {
          next.due_month = "";
        }
      }
      return next;
    });
    if (errors[key]) setErrors((old) => ({ ...old, [key]: "" }));
  };

  const needsDueDay = form.frequency && form.frequency !== "one_time";
  const needsDueMonth =
    form.frequency &&
    ["quarterly", "half_yearly", "annual"].includes(form.frequency);

  const validate = () => {
    const e = {};
    REQUIRED_BASE.forEach((f) => {
      const val = form[f];
      if (val === null || val === undefined || String(val).trim() === "")
        e[f] = "Required";
    });
    if (needsDueDay && !form.due_day) e.due_day = "Required";
    if (needsDueMonth && !form.due_month) e.due_month = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Null out irrelevant fields
      if (payload.frequency === "one_time") {
        payload.due_day = null;
        payload.due_month = null;
      } else if (payload.frequency === "monthly") {
        payload.due_month = null;
      }
      // Coerce numeric text fields — if empty, fall back to defaults
      payload.sla_days =
        payload.sla_days === "" || payload.sla_days == null
          ? 7
          : parseInt(payload.sla_days, 10);
      payload.create_before_days =
        payload.create_before_days === "" || payload.create_before_days == null
          ? 5
          : parseInt(payload.create_before_days, 10);
      if (isEdit) payload.id = data.id;
      const url = isEdit ? "/services/update.php" : "/services/create.php";
      await api.post(url, payload);
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      setSubmitError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const preview = getCyclePreview(form.frequency, form.due_month, form.due_day);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Service Name *"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Service Category"
            value={form.service_type}
            onChange={(e) => setField("service_type", e.target.value)}
          >
            <MenuItem value="">Select category</MenuItem>
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Frequency *"
            value={form.frequency}
            onChange={(e) => setField("frequency", e.target.value)}
            error={!!errors.frequency}
            helperText={errors.frequency}
          >
            <MenuItem value="">Select frequency</MenuItem>
            {FREQUENCIES.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Default Price *"
            value={form.default_price}
            onChange={(e) => setField("default_price", e.target.value)}
            error={!!errors.default_price}
            helperText={errors.default_price}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* DUE MONTH (quarterly / half_yearly / annual) */}
        {needsDueMonth && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label={
                form.frequency === "annual"
                  ? "Due Month *"
                  : form.frequency === "half_yearly"
                    ? "First Cycle Month *"
                    : "Quarter Start Month *"
              }
              value={form.due_month || ""}
              onChange={(e) => setField("due_month", e.target.value)}
              error={!!errors.due_month}
              helperText={errors.due_month}
            >
              <MenuItem value="">Select month</MenuItem>
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {/* DUE DAY (all except one_time) */}
        {needsDueDay && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Due Day *"
              value={form.due_day || ""}
              onChange={(e) => setField("due_day", e.target.value)}
              error={!!errors.due_day}
              helperText={errors.due_day}
            >
              <MenuItem value="">Select day</MenuItem>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {/* CYCLE PREVIEW BANNER */}
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "action.hover",
              borderStyle: "dashed",
            }}
          >
            {form.frequency === "one_time" ? (
              <InfoOutlinedIcon color="info" fontSize="small" />
            ) : (
              <EventRepeatIcon color="primary" fontSize="small" />
            )}
            <Typography variant="body2" color="text.secondary">
              {preview}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Generate Job Before"
            value={form.create_before_days}
            onChange={(e) => setField("create_before_days", e.target.value)}
          >
            {[5, 7, 10, 15, 20, 30, 40].map((d) => (
              <MenuItem key={d} value={d}>
                {d} Days
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="SLA Days"
            value={form.sla_days ?? ""}
            onChange={(e) => setField("sla_days", e.target.value)}
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Default Manager"
            value={form.default_manager_id || ""}
            onChange={(e) => setField("default_manager_id", e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            {managers.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Status"
            value={form.is_active}
            onChange={(e) => setField("is_active", e.target.value)}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.audit_required == 1}
                onChange={(e) =>
                  setField("audit_required", e.target.checked ? 1 : 0)
                }
              />
            }
            label="Audit Required"
          />
        </Grid>
      </Grid>

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 3, justifyContent: "flex-end" }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
        >
          {saving ? "Saving…" : isEdit ? "Update Service" : "Add Service"}
        </Button>
      </Stack>

      {isEdit && data?.id && (
        <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <AccountTreeIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Workflow Configuration
            </Typography>
          </Stack>
          <ServiceWorkflow serviceId={data.id} />
        </Paper>
      )}
    </Box>
  );
}
          